import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import NewsletterSignup from "../components/NewsletterSignup";
import Seo from "../components/Seo";
import { blogCategories, blogPosts } from "../data/blogPosts";
import "../styles/proof-content.css";

export default function InsightsPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredPosts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return blogPosts.filter((post) => {
      const matchesCategory =
        activeCategory === "All" ||
        post.category === activeCategory ||
        post.tags.includes(activeCategory);

      const searchable = [
        post.title,
        post.excerpt,
        post.category,
        ...post.tags,
      ]
        .join(" ")
        .toLowerCase();

      return matchesCategory && (!term || searchable.includes(term));
    });
  }, [activeCategory, searchTerm]);

  const featuredPost = blogPosts.find((post) => post.featured) || blogPosts[0];

  return (
    <div className="site-page proof-page">
      <Seo
        title="Data Apparatus Blog | Data Engineering, Architecture, Governance and AI"
        description="Practical Data Apparatus articles on data engineering, platform architecture, governance, AI readiness, integration, automation, analytics and trusted data delivery."
        canonicalPath="/blog"
      />

      <section className="blog-index-hero">
        <div className="blog-index-hero-inner">
          <div>
            <span className="eyebrow">Data Apparatus blog</span>
            <h1>Practical notes on data engineering, architecture, governance and AI readiness.</h1>
            <p>
              Long-form articles for teams building reliable data platforms, governed
              analytics, integration patterns, automation workflows and AI-ready operating
              foundations.
            </p>
          </div>
          <NewsletterSignup compact />
        </div>
      </section>

      <section className="blog-index-section">
        <div className="blog-index-layout">
          <aside className="blog-filter-panel">
            <label className="blog-search">
              Search articles
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search architecture, governance, AI..."
              />
            </label>

            <div className="blog-category-list" aria-label="Blog categories">
              <span>Topics</span>
              {blogCategories.map((category) => (
                <button
                  type="button"
                  className={activeCategory === category ? "active" : ""}
                  onClick={() => setActiveCategory(category)}
                  key={category}
                >
                  {category}
                </button>
              ))}
            </div>
          </aside>

          <div className="blog-post-list">
            {featuredPost && activeCategory === "All" && !searchTerm && (
              <article className="blog-featured-card">
                <span>{featuredPost.category}</span>
                <h2>{featuredPost.title}</h2>
                <p>{featuredPost.excerpt}</p>
                <div className="blog-card-meta">
                  <em>{featuredPost.date}</em>
                  <em>{featuredPost.readTime}</em>
                </div>
                <div className="proof-tag-row">
                  {featuredPost.tags.map((tag) => (
                    <button
                      type="button"
                      onClick={() => setActiveCategory(tag)}
                      key={tag}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <Link to={featuredPost.href} className="primary-btn insight-read-link">
                  Read the article
                </Link>
              </article>
            )}

            <div className="blog-results-header">
              <h2>{filteredPosts.length} article{filteredPosts.length === 1 ? "" : "s"}</h2>
              <p>
                {activeCategory === "All"
                  ? "Browse Data Apparatus articles across engineering, architecture, governance and AI."
                  : `Filtered by ${activeCategory}.`}
              </p>
            </div>

            <div className="blog-card-grid">
              {filteredPosts.map((post) => (
                <article className="blog-card" key={post.slug}>
                  <span>{post.category}</span>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <div className="blog-card-meta">
                    <em>{post.date}</em>
                    <em>{post.readTime}</em>
                  </div>
                  <div className="blog-card-tags">
                    {post.tags.slice(0, 4).map((tag) => (
                      <button
                        type="button"
                        onClick={() => setActiveCategory(tag)}
                        key={tag}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  <Link to={post.href}>Read article</Link>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="blog-empty-state">
                <h3>No articles found</h3>
                <p>Try a different topic or clear the search field.</p>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => {
                    setActiveCategory("All");
                    setSearchTerm("");
                  }}
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="blog-subscribe-band">
        <NewsletterSignup />
      </section>

      <Footer />
    </div>
  );
}
